/**
 * Centralized App Footer Component
 * Single source of truth for footer branding across all pages
 * 
 * Variants:
 * - auth: For login, password reset, change password pages (compact)
 * - main: For main app layout (full width, fixed)
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';

// App version from environment
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
const BUILD_HASH = import.meta.env.VITE_BUILD_HASH || '';

// Brand colors - centralized
const BRAND = {
  green: '#198a4b',
  greenDark: '#0d5c2e',
  orange: '#ef7d00',
  gray: '#475569',
  grayLight: '#64748b',
};

interface AppFooterProps {
  variant?: 'auth' | 'main';
}

/**
 * Powered by ISWE Analytics branding component
 * Uses SVG logo for consistent branding with subtle background
 */
const IsweAnalyticsBranding: React.FC<{ height?: number }> = ({ height = 20 }) => (
  <Box
    component="a"
    href="https://iswesolutions.com"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      bgcolor: 'rgba(25, 138, 75, 0.06)',
      border: '1px solid rgba(25, 138, 75, 0.12)',
      flexShrink: 0,
      minWidth: 'fit-content',
      '&:hover': {
        opacity: 0.8,
        bgcolor: 'rgba(25, 138, 75, 0.1)',
      },
    }}
  >
    <Box
      component="img"
      src="/powered-by-iswe.svg"
      alt="Powered by ISWE Analytics"
      sx={{ 
        height,
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
      }}
    />
  </Box>
);

/**
 * Auth page footer (login, password reset, etc.)
 * Centered: copyright on top, powered by ISWE below
 */
const AuthFooter: React.FC = () => (
  <Box
    sx={{
      mt: 2.5,
      pt: 1.5,
      borderTop: '1px solid #f1f5f9',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Typography
      sx={{
        color: BRAND.gray,
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      © {new Date().getFullYear()} Zambia National Public Health Institute
    </Typography>
    <IsweAnalyticsBranding height={38} />
  </Box>
);

/**
 * Main app footer (fixed at bottom)
 * Desktop: Version left | Copyright center | Powered by right
 * Mobile: Stacked - copyright then powered by
 */
const MainFooter: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        borderTop: '2px solid #e5e7eb',
        padding: isMobile ? '8px 12px' : '10px 24px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'space-between',
        background: 'linear-gradient(180deg, #f8faf9 0%, #f0f4f2 100%)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 1200,
        height: isMobile ? 'auto' : 64,
        minHeight: isMobile ? 56 : 64,
        gap: isMobile ? 0.5 : 0,
      }}
    >
      {/* Left - Version badge (desktop only) */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: 'rgba(13, 92, 46, 0.08)',
            border: '1px solid rgba(13, 92, 46, 0.12)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: BRAND.greenDark,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          >
            v{APP_VERSION}
          </Typography>
          {BUILD_HASH && (
            <Typography
              variant="caption"
              sx={{
                color: BRAND.grayLight,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
              }}
            >
              {BUILD_HASH.substring(0, 7)}
            </Typography>
          )}
        </Box>
      )}

      {/* Center - Copyright */}
      <Typography
        variant="body2"
        sx={{
          color: '#023f1aff',
          fontSize: isMobile ? '0.7rem' : '0.85rem',
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        © {new Date().getFullYear()} {isMobile ? 'ZNPHI' : 'Zambia National Public Health Institute'}
        {isMobile && ` • v${APP_VERSION}`}
      </Typography>

      {/* Right - Powered by ISWE */}
      <IsweAnalyticsBranding height={isMobile ? 32 : 38} />
    </Box>
  );
};

/**
 * Main AppFooter component
 */
export const AppFooter: React.FC<AppFooterProps> = ({ variant = 'auth' }) => {
  if (variant === 'main') {
    return <MainFooter />;
  }
  return <AuthFooter />;
};

export default AppFooter;
